import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Waves, ChevronDown, LogOut, LayoutDashboard, User, Heart, LifeBuoy } from "lucide-react";

// Graphical SVG country flags (crisp on retina, no emoji fallback needed)
function Flag({ code, size = 18 }) {
  const w = size;
  const h = Math.round(size * 0.7);
  const wrap = {
    display: "inline-block",
    width: `${w}px`,
    height: `${h}px`,
    borderRadius: "3px",
    overflow: "hidden",
    border: "1px solid rgba(0,0,0,.1)",
    boxShadow: "0 1px 2px rgba(0,0,0,.08)",
    verticalAlign: "middle",
    lineHeight: 0,
    flexShrink: 0,
  };
  if (code === "it") {
    return (
      <span style={wrap} aria-label="Italiano">
        <svg viewBox="0 0 3 2" width={w} height={h} preserveAspectRatio="none">
          <rect x="0" width="1" height="2" fill="#008C45" />
          <rect x="1" width="1" height="2" fill="#F4F5F0" />
          <rect x="2" width="1" height="2" fill="#CD212A" />
        </svg>
      </span>
    );
  }
  if (code === "en") {
    return (
      <span style={wrap} aria-label="English">
        <svg viewBox="0 0 60 42" width={w} height={h} preserveAspectRatio="none">
          <rect width="60" height="42" fill="#012169" />
          <path d="M0,0 L60,42 M60,0 L0,42" stroke="#FFF" strokeWidth="8" />
          <path d="M30,0 v42 M0,21 h60" stroke="#FFF" strokeWidth="12" />
          <path d="M30,0 v42 M0,21 h60" stroke="#C8102E" strokeWidth="6" />
        </svg>
      </span>
    );
  }
  return (
    <span style={wrap} aria-label="Deutsch">
      <svg viewBox="0 0 3 3" width={w} height={h} preserveAspectRatio="none">
        <rect y="0" width="3" height="1" fill="#000" />
        <rect y="1" width="3" height="1" fill="#DD0000" />
        <rect y="2" width="3" height="1" fill="#FFCE00" />
      </svg>
    </span>
  );
}

const langLabels = { it: "ITA", en: "ENG", de: "DE" };

export default function Layout({ children }) {
  const { user, logout, t, lang, changeLang } = useApp();
  const nav = useNavigate();
  const dashboardPath = user?.role === "admin" ? "/admin" : user?.role === "owner" ? "/owner" : "/guest";
  return (
    <div className="min-h-screen flex flex-col">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-9 h-9 rounded-full bg-[#005C7A] flex items-center justify-center"><Waves className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
            <span className="font-display text-xl font-bold text-[#005C7A]">PoolPass</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <Link to="/search" className="hover:text-[#005C7A] transition-colors" data-testid="nav-search">{t("nav.search")}</Link>
            <Link to="/map" className="hover:text-[#005C7A] transition-colors" data-testid="nav-map">{t("nav.map")}</Link>
            {user && user.role === "guest" && <Link to="/favorites" className="hover:text-[#005C7A] transition-colors flex items-center gap-1" data-testid="nav-favorites"><Heart className="w-3.5 h-3.5" /> {t("nav.favorites")}</Link>}
            <Link to="/support" className="hover:text-[#005C7A] transition-colors" data-testid="nav-support">{t("nav.support")}</Link>
            {!user && <Link to="/register/hotel" className="hover:text-[#005C7A] transition-colors" data-testid="nav-owner">{t("nav.owner")}</Link>}
          </nav>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-border hover:bg-white transition" data-testid="lang-switcher">
                  <Flag code={lang} size={18} />
                  <span className="font-medium">{langLabels[lang]}</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {["it", "en", "de"].map((l) => (
                  <DropdownMenuItem key={l} onClick={() => changeLang(l)} data-testid={`lang-${l}`} className="gap-2">
                    <Flag code={l} size={18} /> <span>{langLabels[l]}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-full border border-border hover:bg-white transition" data-testid="user-menu">
                    <User className="w-4 h-4" /><span className="font-medium max-w-[110px] truncate">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => nav(dashboardPath)} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> {user.role === "admin" ? t("nav.admin") : t("nav.dashboard")}
                  </DropdownMenuItem>
                  {user.role === "guest" && (
                    <DropdownMenuItem onClick={() => nav("/favorites")} data-testid="menu-favorites">
                      <Heart className="w-4 h-4 mr-2" /> {t("nav.favorites")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => nav("/support")} data-testid="menu-support">
                    <LifeBuoy className="w-4 h-4 mr-2" /> {t("nav.support")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button onClick={() => nav("/auth")} className="rounded-full bg-[#005C7A] hover:bg-[#004A63] text-white" data-testid="nav-login">{t("nav.login")}</Button>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t border-border bg-white/40 mt-16">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Waves className="w-4 h-4 text-[#005C7A]" />
            <span className="font-display text-lg text-[#1A202C]">PoolPass</span>
            <span className="ml-3">— {t("footer.tagline")}</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/auth?role=owner" className="hover:text-[#005C7A]" data-testid="footer-hotel-area">{t("footer.hotelArea")}</Link>
            <span className="text-border">·</span>
            <Link to="/support" className="hover:text-[#005C7A]">{t("nav.support")}</Link>
            <span>© 2026 PoolPass</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
